import {FormControl} from "@luciad/ria-sample-common/components/FormControl";
import {useMapContext} from "@luciad/ria-sample-common/hooks/useMapContext";
import {useToastContext} from "@luciad/ria-sample-common/hooks/useToast";
import {WMTSCapabilities} from "@luciad/ria/model/capabilities/WMTSCapabilities";
import {WMTSCapabilitiesLayer, WMTSCapabilitiesLayerStyle} from "@luciad/ria/model/capabilities/WMTSCapabilitiesLayer";
import {WebGLMap} from "@luciad/ria/view/WebGLMap";
import React, {ChangeEvent, useState} from "react";
import {addLayer} from "../DataFormatsUtil";
import {useWizard} from "../hooks/useWizard";
import {WMTSDataLoader} from "../loaders/WMTSDataLoader";
import {ConnectToURLPage} from "./ConnectToURLPage";
import {WizardPreviousNextButtons} from "./WizardPreviousNextButtons";

interface Props {
  onPrevious: () => void;
  onReset: () => void;
}

type WMTSWizardPageKey = "enterUrl" | "loading" | "selectLayer";

export const ConnectToWMTSWizard = ({onPrevious, onReset}: Props) => {
  const {currentPageKey, previousPageKey, changePage, resetWizard} = useWizard<WMTSWizardPageKey>("enterUrl", onReset);
  const [capabilities, setCapabilities] = useState<WMTSCapabilities | null>(null);
  const [selectedLayer, setSelectedLayer] = useState<WMTSCapabilitiesLayer | null>();
  const [selectedStyle, setSelectedStyle] = useState<WMTSCapabilitiesLayerStyle | null>(null);

  const {map} = useMapContext();
  const {showErrorToast} = useToastContext();

  const handleUrlSubmit = (url: string) => {
    changePage("loading");
    WMTSCapabilities.fromURL(url).then(capabilities => {
      setCapabilities(capabilities);
      setSelectedLayer(capabilities.layers[0]);
      setSelectedStyle(capabilities.layers[0].styles[0]);
      changePage("selectLayer");
    }).catch(error => {
      showErrorToast("Could not connect with the WMTS server", error);
      changePage("enterUrl");
    });
  }

  const handleSelectedLayerChange = (ev: ChangeEvent<HTMLSelectElement>) => {
    if (capabilities) {
      const selectedLayerId = ev.target.value;
      const selectedLayer = capabilities.layers.find(l => l.identifier === selectedLayerId);
      setSelectedLayer(selectedLayer);
      setSelectedStyle(selectedLayer?.styles[0] ?? null);
    }
  }

  const handleSelectedStyleChange = (ev: ChangeEvent<HTMLSelectElement>) => {
    if (capabilities) {
      const selectedStyleId = ev.target.value;
      const selectedStyle = selectedLayer?.styles.find(s => s.identifier === selectedStyleId);
      if (selectedStyle) {
        setSelectedStyle(selectedStyle);
      }
    }
  }

  const handleSelect = () => {
    if (capabilities && selectedLayer && selectedStyle) {
      try {
        const referenceOption = map instanceof WebGLMap ? {} : {reference: map.reference};
        const layer = WMTSDataLoader.createLayerFromCapabilities(capabilities, selectedLayer, selectedStyle, referenceOption);
        addLayer(map, layer).catch(error => showErrorToast("Error while adding WMTS layer", error));
      } catch (error) {
        showErrorToast("Error while adding WMTS layer", error);
      } finally {
        resetWizard();
      }
    }
  }

  switch (currentPageKey) {
  case "enterUrl": {
    return <ConnectToURLPage defaultValue="https://sampleservices.luciad.com/wmts"
                             onSubmit={handleUrlSubmit}
                             onPrevious={onPrevious}
    />
  }
  case "loading": {
    return <h1>Loading WMTS capabilities...</h1>;
  }
  case "selectLayer": {
    if (!selectedLayer || !selectedStyle || !capabilities) {
      throw new Error("Invalid state");
    }
    return (
        <div>
          <div>
            <FormControl>
              <label htmlFor="layer">
                Layer
              </label>
              <select value={selectedLayer.identifier} onChange={handleSelectedLayerChange}>
                {capabilities.layers.map(layer => {
                  return <option key={layer.identifier} value={layer.identifier}>{layer.title}</option>
                })}
              </select>
            </FormControl>
            <FormControl>
              <label htmlFor="style">
                Style
              </label>
              <select value={selectedStyle.identifier} onChange={handleSelectedStyleChange}>
                {selectedLayer.styles.map(style => {
                  return (<option key={style.identifier} value={style.identifier}>
                    {style.title}
                  </option>)
                })}
              </select>
            </FormControl>
          </div>
          <WizardPreviousNextButtons onPrevious={() => changePage(previousPageKey)}
                                     onNext={handleSelect}
                                     nextLabel="Add"
          />
        </div>
    );
  }
  }
}